import { type VariantProps, cva } from 'class-variance-authority'
import React from 'react'

import Label from './Label'

const segmentedControlVariants = cva('inline-flex rounded-lg bg-gray-100 p-1', {
    variants: {
        size: {
            medium: 'h-12',
            small: 'h-9',
        },
    },
    defaultVariants: {
        size: 'medium',
    },
})

const segmentVariants = cva(
    'flex flex-1 gap-1 min-w-32 items-center justify-center px-3 rounded-lg border-2 transition-colors duration-200 ease-in-out \
    focus-visible:outline-hidden focus-visible:border-info-400',
    {
        variants: {
            active: {
                true: 'bg-white text-secondary-400 border-secondary-400 \
                    hover:bg-linear-to-b hover:from-black/4 hover:to-black/4 hover:bg-white \
                    active:bg-linear-to-b active:from-black/8 active:to-black/8 active:bg-white',
                false: 'text-gray-500 border-gray-100 hover:bg-black/4 active:bg-black/8',
            },
            compact: {
                true: 'min-w-fit',
            },
        },
        defaultVariants: {
            active: false,
            compact: false,
        },
    }
)

interface Segment {
    label: string
    value: string
    icon?: React.ReactNode
}

interface SegmentedControlProps extends VariantProps<typeof segmentedControlVariants> {
    segments: Segment[]
    value: string
    onChange: (value: string) => void
    className?: string
    compact?: boolean
}

const SegmentedControl: React.FC<SegmentedControlProps> = ({
    segments,
    value,
    onChange,
    size,
    className,
    compact,
    ...props
}) => {
    return (
        <div {...props} className={segmentedControlVariants({ size, className })}>
            {segments.map((segment) => (
                <button
                    key={segment.value}
                    data-testid={segment.value}
                    className={segmentVariants({ active: value === segment.value, compact })}
                    onClick={() => onChange(segment.value)}
                >
                    {segment.icon && <span>{segment.icon}</span>}
                    <Label size="sm" className="cursor-pointer truncate">
                        {segment.label}
                    </Label>
                </button>
            ))}
        </div>
    )
}

export default SegmentedControl
